/**
 * Comparar un período con el anterior, sin inventar el anterior.
 *
 * Un KPI suelto no dice nada: «137 operaciones» puede ser un buen día o el
 * principio de una caída. Lo que da sentido es «▲ 12% contra la semana pasada».
 *
 * El riesgo, que ya se pagó una vez (lección L31, regla F11): el pipe analítico
 * tiene datos desde el 11 de agosto, y **una comparación cuya mitad anterior
 * cae fuera de esa cobertura mezcla dos realidades distintas** — el período
 * viejo se ve vacío no porque no pasara nada, sino porque no estábamos
 * mirando. En un panel eso aparece como un crecimiento espectacular que nunca
 * ocurrió.
 */

/** Debajo de esta base, el porcentaje mide ruido y no tendencia. */
const BASE_MINIMA = 10;

export interface Comparacion {
  actual: number;
  anterior: number;
  /** Diferencia relativa en puntos porcentuales, redondeada a un decimal. */
  cambio: number;
  sentido: 'sube' | 'baja' | 'estable';
}

/**
 * Parte la serie al medio y compara las dos mitades.
 *
 * Devuelve `null` —y el panel no dibuja nada— cuando comparar sería mentir:
 *
 * - **Serie demasiado corta.** Con menos de 4 puntos las mitades son de 1 o 2
 *   días y el resultado es ruido con cara de tendencia.
 * - **La mitad anterior cae fuera de la cobertura del pipe.** Es el caso de
 *   L31: se vería un salto enorme que sólo refleja cuándo empezamos a medir.
 * - **El período anterior fue cero.** No existe el porcentaje de crecimiento
 *   sobre cero, y mostrar «+∞%» o «+100%» es inventarlo.
 * - **El período anterior es demasiado chico.** Un porcentaje sobre una base
 *   mínima es ruido con cara de tendencia: con `anterior = 3`, un solo evento
 *   mueve la cifra 33 puntos. El panel de CAMU del Prado mostró «▲ 5.566,7%»
 *   comparando 173 contra 3 — correcto y sin ningún contenido: era el arranque
 *   del tenant, no crecimiento.
 */
export function compararConPeriodoAnterior(
  puntos: ReadonlyArray<{ date: string; valor: number }>,
  cobertura?: { from: string | null; partial: boolean },
): Comparacion | null {
  if (puntos.length < 4) return null;

  // Con una cantidad impar de días las mitades quedan desiguales, y entonces una
  // serie PLANA reporta crecimiento: 7 días se parten en 3 y 4, o sea +33% sin
  // que haya pasado nada. La pestaña "7d" entrega exactamente 7 puntos y `byDay`
  // omite los días sin actividad, así que el caso es rutinario, no exótico. Se
  // descarta el día más viejo —nunca el más nuevo, que es el que se está
  // mirando— para que ambas mitades cubran lo mismo.
  const parejos = puntos.length % 2 === 0 ? puntos : puntos.slice(1);

  const mitad = parejos.length / 2;
  const anteriores = parejos.slice(0, mitad);
  const actuales = parejos.slice(mitad);

  // El día más viejo de la mitad vieja tiene que estar cubierto por el pipe. Si
  // no, su "cero" significa "no mirábamos", no "no pasó nada". Se busca el
  // mínimo en vez de tomar el primero: si la API mandara los puntos
  // desordenados, `[0]` dejaría pasar una comparación que cruza el inicio de la
  // medición.
  const desde = cobertura?.from;
  const masViejo = anteriores.reduce((a, b) => (b.date < a.date ? b : a)).date;
  if (desde && masViejo < desde) return null;

  const suma = (xs: ReadonlyArray<{ valor: number }>) => xs.reduce((a, b) => a + b.valor, 0);
  const anterior = suma(anteriores);
  const actual = suma(actuales);

  if (anterior === 0) return null;

  // Piso de la base. Un evento de más mueve el porcentaje `100/anterior` puntos,
  // así que por debajo de 10 un solo dato lo empuja más de 10 puntos y el número
  // deja de medir la tendencia para medir el ruido. Es deliberadamente bajo: un
  // tenant chico con 10 → 20 sí dobló, y eso se muestra.
  if (anterior < BASE_MINIMA) return null;

  const cambio = Math.round(((actual - anterior) / anterior) * 1000) / 10;
  // Menos de medio punto no es una tendencia: una flecha que cambia de color
  // por ruido se termina ignorando.
  const sentido = Math.abs(cambio) < 0.5 ? 'estable' : cambio > 0 ? 'sube' : 'baja';

  return { actual, anterior, cambio, sentido };
}

/** «▲ 12,4%» / «▼ 3,1%» / «sin cambio», para mostrar junto al número. */
export function textoDeCambio(c: Comparacion): string {
  if (c.sentido === 'estable') return 'sin cambio';
  const flecha = c.sentido === 'sube' ? '▲' : '▼';
  return `${flecha} ${Math.abs(c.cambio).toLocaleString('es-CO')}%`;
}
